import * as config from "../constants/config";
import * as validationHelper from "../helpers/validationHelper";

/**
 * @param   {String}    hexColor
 * @returns {{red: Number, green Number, blue: Number}}
 */
const convertHexToRgb = (hexColor) => {
    if (hexColor === undefined || hexColor === null) {
        return null;
    }

    let result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hexColor.toLowerCase());

    if (result) {
        return {
            red: parseInt(result[1], 16),
            green: parseInt(result[2], 16),
            blue: parseInt(result[3], 16)
        };
    }

    return null;
};

/**
 * @param   {Number}    red
 * @param   {Number}    green
 * @param   {Number}    blue
 * @returns {String}
 */
const convertRgbToHex = (red, green, blue) => {
    return (
        "#" +
        (red < 16 ? "0" + red.toString(16) : red.toString(16)) +
        (green < 16 ? "0" + green.toString(16) : green.toString(16)) +
        (blue < 16 ? "0" + blue.toString(16) : blue.toString(16))
    );
};

/**
 * @param   {String}    hexColor
 * @returns {{hue: Number, sat: Number, lum: Number}}
 */
const convertRgbToHsl = (hexColor) => {
    let rgb = convertHexToRgb(hexColor);

    if (rgb === null) {
        return {
            hue: 0,
            sat: 0,
            lum: 0
        };
    }

    let r = rgb.red / 255;
    let g = rgb.green / 255;
    let b = rgb.blue / 255;

    let max = Math.max(r, g, b);
    let min = Math.min(r, g, b);

    let lum = (max + min) / 2;
    let hue;
    let sat;
    if (max === min) {
        hue = 0;
        sat = 0;
    } else {
        let c = max - min;
        sat = c / (1 - Math.abs(2 * lum - 1));
        switch (max) {
            case r:
                hue = (g - b) / c + (g < b ? 6 : 0);
                break;
            case g:
                hue = (b - r) / c + 2;
                break;
            case b:
                hue = (r - g) / c + 4;
                break;
            default:
                break;
        }
    }

    hue = Math.round(hue * 60);
    sat = Math.round(sat * 100);
    lum = Math.round(lum * 100);

    return {
        hue: hue,
        sat: sat,
        lum: lum
    };
};

/**
 * @param   {String}    hexColor
 * @returns {Number}
 */
export const getColorIntensity = (hexColor) => {
    if (hexColor === undefined || hexColor === null) {
        return 255;
    }

    let rgb = convertHexToRgb(hexColor);

    if (rgb === null) {
        return 255;
    }

    return 0.2126 * rgb.red + 0.7152 * rgb.green + 0.0722 * rgb.blue;
};

/**
 * @param   {String}    hexColor
 * @returns {Number}
 */
export const getColorBrightness = (hexColor) => {
    let hsl = convertRgbToHsl(hexColor);
    return hsl.lum;
};

/**
 * @param   {Array}     formula     array of hex colors
 * @returns {String}
 */
export const getFormulaColor = (formula) => {
    if (formula.length === 0) {
        return config.DEFAULT_RGB;
    }

    let rgb = null;
    let amount = 0;
    let lum = 101;

    let preview;

    let colorant;
    for (colorant of formula) {
        preview = convertRgbToHsl(colorant.rgb);

        if (
            rgb === null ||
            amount / colorant.amount <= 0.7 ||
            (lum > preview.lum && amount / colorant.amount <= 1.25)
        ) {
            amount = colorant.amount;
            lum = preview.lum;
            rgb = colorant.rgb;
        }
    }

    if (rgb === null) {
        return config.DEFAULT_RGB;
    }

    return rgb;
};

const TRISTIMULUS_CIE_1964 = {
    A: [111.144, 100, 35.2],
    C: [97.285, 100, 116.145],
    D50: [96.72, 100, 81.427],
    D55: [95.799, 100, 90.926],
    D65: [94.811, 100, 107.304],
    D75: [94.416, 100, 120.641]
};

const DEFAULT_TRISTIMULUS = TRISTIMULUS_CIE_1964["D65"];

const CIE_E = 216.0 / 24389.0;

/**
 * @param   {{xyzX: Number, xyzY: Number, xyzZ: Number}}    xyz
 * @returns {{labL: Number, labA: Number, labB: Number}}
 */
export const xyzToLab = (xyz, illum = DEFAULT_TRISTIMULUS) => {
    if (!xyz || !validationHelper.isXyzValueValid(xyz)) {
        return null;
    }

    let tempX = Number(xyz.xyzX) / illum[0];
    let tempY = Number(xyz.xyzY) / illum[1];
    let tempZ = Number(xyz.xyzZ) / illum[2];

    if (tempX > CIE_E) {
        tempX = tempX ** (1.0 / 3.0);
    } else {
        tempX = 7.787 * tempX + 16.0 / 116.0;
    }

    if (tempY > CIE_E) {
        tempY = tempY ** (1.0 / 3.0);
    } else {
        tempY = 7.787 * tempY + 16.0 / 116.0;
    }

    if (tempZ > CIE_E) {
        tempZ = tempZ ** (1.0 / 3.0);
    } else {
        tempZ = 7.787 * tempZ + 16.0 / 116.0;
    }
    return {
        labL: 116.0 * tempY - 16.0,
        labA: 500.0 * (tempX - tempY),
        labB: 200.0 * (tempY - tempZ)
    };
};

/**
 * @param   {{labL: Number, labA: Number, labB: Number}}    lab
 * @returns {{xyzX: Number, xyzY: Number, xyzZ: Number}}
 */
export const labToXyz = (lab, illum = DEFAULT_TRISTIMULUS) => {
    if (!lab || !validationHelper.isLabValueValid(lab)) {
        return null;
    }

    let xyzY = (Number(lab.labL) + 16.0) / 116.0;
    let xyzX = Number(lab.labA) / 500.0 + xyzY;
    let xyzZ = xyzY - Number(lab.labB) / 200.0;

    if (xyzY ** 3 > CIE_E) {
        xyzY = xyzY ** 3;
    } else {
        xyzY = (xyzY - 16.0 / 116.0) / 7.787;
    }

    if (xyzX ** 3 > CIE_E) {
        xyzX = xyzX ** 3;
    } else {
        xyzX = (xyzX - 16.0 / 116.0) / 7.787;
    }

    if (xyzZ ** 3 > CIE_E) {
        xyzZ = xyzZ ** 3;
    } else {
        xyzZ = (xyzZ - 16.0 / 116.0) / 7.787;
    }
    const res = {
        xyzX: illum[0] * xyzX,
        xyzY: illum[1] * xyzY,
        xyzZ: illum[2] * xyzZ
    };
    return {
        xyzX: res.xyzX || 0,
        xyzY: res.xyzY || 0,
        xyzZ: res.xyzZ || 0
    };
};

/**
 * @param   {Number}    c
 * @returns {Number}
 */
export const sRgbNonlinearTransformation = (c) => {
    if (c <= 0.04045) {
        return c / 12.92;
    }
    return Math.pow((c + 0.055) / 1.055, 2.4);
};

/**
 * @param   {{xyzX: Number, xyzY: Number, xyzZ: Number}}    xyz
 * @returns {{rgbR: Number, rgbG: Number, rgbB: Number}}
 */
export const xyzToRgb = (xyz) => {
    const white = [100, 100, 100];

    var x = Number(xyz.xyzX) / white[0],
        y = Number(xyz.xyzY) / white[1],
        z = Number(xyz.xyzZ) / white[2],
        r,
        g,
        b;

    r = x * 3.240969941904521 + y * -1.537383177570093 + z * -0.498610760293;
    g = x * -0.96924363628087 + y * 1.87596750150772 + z * 0.041555057407175;
    b = x * 0.055630079696993 + y * -0.20397695888897 + z * 1.056971514242878;

    r = r > 0.0031308 ? 1.055 * Math.pow(r, 1.0 / 2.4) - 0.055 : (r = r * 12.92);

    g = g > 0.0031308 ? 1.055 * Math.pow(g, 1.0 / 2.4) - 0.055 : (g = g * 12.92);

    b = b > 0.0031308 ? 1.055 * Math.pow(b, 1.0 / 2.4) - 0.055 : (b = b * 12.92);

    r = Math.min(Math.max(0, r), 1);
    g = Math.min(Math.max(0, g), 1);
    b = Math.min(Math.max(0, b), 1);

    return {
        rgbR: Math.round(r * 255),
        rgbG: Math.round(g * 255),
        rgbB: Math.round(b * 255)
    };
};

/**
 * @param   {Number}    r
 * @param   {Number}    g
 * @param   {Number}    b
 * @returns {String}
 */
const rgbToXyz2 = (r, g, b) => {
    let tmpR = r / 255;
    let tmpG = g / 255;
    let tmpB = b / 255;

    if (tmpR > 0.04045) {
        tmpR = Math.pow((tmpR + 0.055) / 1.055, 2.4);
    } else {
        tmpR = tmpR / 12.92;
    }

    if (tmpG > 0.04045) {
        tmpG = Math.pow((tmpG + 0.055) / 1.055, 2.4);
    } else {
        tmpG = tmpG / 12.92;
    }

    if (tmpB > 0.04045) {
        tmpB = Math.pow((tmpB + 0.055) / 1.055, 2.4);
    } else {
        tmpB = tmpB / 12.92;
    }

    tmpR = tmpR * 100;
    tmpG = tmpG * 100;
    tmpB = tmpB * 100;

    return [
        Math.round((tmpR * 0.4124 + tmpG * 0.3576 + tmpB * 0.1805) * 100) / 100,
        Math.round((tmpR * 0.2126 + tmpG * 0.7152 + tmpB * 0.0722) * 100) / 100,
        Math.round((tmpR * 0.0193 + tmpG * 0.1192 + tmpB * 0.9505) * 100) / 100
    ];
};

/**
 * @param   {Array}     xyz
 * @returns {String}
 */
const xyzToRgb2 = (xyz) => {
    let tmpX = xyz[0] / 100;
    let tmpY = xyz[1] / 100;
    let tmpZ = xyz[2] / 100;

    let tmpR = tmpX * 3.2406 + tmpY * -1.5372 + tmpZ * -0.4986;
    let tmpG = tmpX * -0.9689 + tmpY * 1.8758 + tmpZ * 0.0415;
    let tmpB = tmpX * 0.0557 + tmpY * -0.204 + tmpZ * 1.057;

    if (tmpR > 0.0031308) {
        tmpR = 1.055 * Math.pow(tmpR, 1 / 2.4) - 0.055;
    } else {
        tmpR = 12.92 * tmpR;
    }

    if (tmpG > 0.0031308) {
        tmpG = 1.055 * Math.pow(tmpG, 1 / 2.4) - 0.055;
    } else {
        tmpG = 12.92 * tmpG;
    }

    if (tmpB > 0.0031308) {
        tmpB = 1.055 * Math.pow(tmpB, 1 / 2.4) - 0.055;
    } else {
        tmpB = 12.92 * tmpB;
    }

    if (tmpR > 1) {
        tmpR = 1;
    } else if (tmpR < 0) {
        tmpR = 0;
    }

    if (tmpG > 1) {
        tmpG = 1;
    } else if (tmpG < 0) {
        tmpG = 0;
    }

    if (tmpB > 1) {
        tmpB = 1;
    } else if (tmpB < 0) {
        tmpB = 0;
    }

    return [Math.round(tmpR * 255), Math.round(tmpG * 255), Math.round(tmpB * 255)];
};

/**
 * @param   {Number}    x
 * @param   {Number}    y
 * @param   {Number}    z
 * @returns {String}
 */
const xyzTolCieLab2 = (x, y, z) => {
    let tmpX = x / 95.047;
    let tmpY = y / 100;
    let tmpZ = z / 108.883;

    if (tmpX > 0.008856) {
        tmpX = Math.pow(tmpX, 0.3333333333333333);
    } else {
        tmpX = 7.787 * tmpX + 0.13793103448275862;
    }

    if (tmpY > 0.008856) {
        tmpY = Math.pow(tmpY, 0.3333333333333333);
    } else {
        tmpY = 7.787 * tmpY + 0.13793103448275862;
    }

    if (tmpZ > 0.008856) {
        tmpZ = Math.pow(tmpZ, 0.3333333333333333);
    } else {
        tmpZ = 7.787 * tmpZ + 0.13793103448275862;
    }

    return [116 * tmpY - 16, 500 * (tmpX - tmpY), 200 * (tmpY - tmpZ)];
};

/**
 * @param   {Array}     lab
 * @returns {String}
 */
const cieLabToXyz2 = (lab) => {
    let tmpY = (lab[0] + 16) / 116;
    let tmpX = lab[1] / 500 + tmpY;
    let tmpZ = tmpY - lab[2] / 200;

    if (Math.pow(tmpX, 3) > 0.008856) {
        tmpX = Math.pow(tmpX, 3);
    } else {
        tmpX = (tmpX - 0.13793103448275862) / 7.787;
    }

    if (Math.pow(tmpY, 3) > 0.008856) {
        tmpY = Math.pow(tmpY, 3);
    } else {
        tmpY = (tmpY - 0.13793103448275862) / 7.787;
    }

    if (Math.pow(tmpZ, 3) > 0.008856) {
        tmpZ = Math.pow(tmpZ, 3);
    } else {
        tmpZ = (tmpZ - 0.13793103448275862) / 7.787;
    }

    return [tmpX * 95.047, tmpY * 100, tmpZ * 108.883];
};

/**
 * @param   {Number}    l
 * @param   {Number}    a
 * @param   {Number}    b
 * @returns {String}
 */
const cieLabToCieLch2 = (l, a, b) => {
    let tmpH = Math.atan2(b, a);

    if (tmpH === 0) {
        tmpH = 0;
    } else if (tmpH > 0) {
        tmpH = (tmpH / Math.PI) * 180;
    } else {
        tmpH = 360 - (Math.abs(tmpH) / Math.PI) * 180;
    }

    return [l, Math.sqrt(Math.pow(a, 2) + Math.pow(b, 2)), tmpH];
};

/**
 * @param   {Array}     lch
 * @returns {String}
 */
const cieLchToCieLab2 = (lch) => {
    return [lch[0], Math.cos(lch[2] * (Math.PI / 180)) * lch[1], Math.sin(lch[2] * (Math.PI / 180)) * lch[1]];
};

/**
 * @param   {Array}     colorantList
 * @param   {Number}    scaling
 * @returns {String}
 */
export const scaleRgb = (r, g, b, scaling) => {
    if (scaling === 1) {
        return convertRgbToHex(r, g, b);
    }

    let xyz = rgbToXyz2(r, g, b);
    let lab = xyzTolCieLab2(xyz[0], xyz[1], xyz[2]);
    let lch = cieLabToCieLch2(lab[0], lab[1], lab[2]);

    if (scaling > 1) {
        lch[0] = lch[0] - Math.round(Math.abs(1 - scaling) * 6);
    } else {
        lch[0] = lch[0] + Math.round(Math.abs(1 - scaling) * 6);
    }

    lab = cieLchToCieLab2(lch);
    xyz = cieLabToXyz2(lab);
    let rgb = xyzToRgb2(xyz);

    return convertRgbToHex(rgb[0], rgb[1], rgb[2]);
};

/**
 * @param   {Array}     colorantList
 * @param   {Number}    scaling
 * @returns {String}
 */
export const generateRgbByColorants = (colorantList, scaling = 1) => {
    if (colorantList.length === 0) {
        return config.DEFAULT_RGB;
    }

    let rgb = null;
    let r = 0;
    let g = 0;
    let b = 0;
    let sum = 0;

    for (let colorant of colorantList) {
        rgb = convertHexToRgb(colorant.rgb);

        if (rgb !== null) {
            r += (colorant.amount + Number(colorant.correction)) * rgb.red;
            g += (colorant.amount + Number(colorant.correction)) * rgb.green;
            b += (colorant.amount + Number(colorant.correction)) * rgb.blue;
            sum += colorant.amount + Number(colorant.correction);
        }
    }

    if (sum === 0) {
        sum = 1;
    }

    r = Math.round(r / sum);

    if (r > 255) {
        r = r % 256;
    }

    g = Math.round(g / sum);

    if (g > 255) {
        g = g % 256;
    }

    b = Math.round(b / sum);

    if (b > 255) {
        b = b % 256;
    }

    return scaleRgb(r, g, b, scaling);
};

/**
 * @param   {String}    hex
 * @param   {Number}    scaling
 * @returns {String}
 */
export const generateRgbByScaling = (hex, scaling = 1) => {
    let rgb = convertHexToRgb(hex);

    if (rgb === null) {
        return config.DEFAULT_RGB;
    }

    return scaleRgb(rgb.red, rgb.green, rgb.blue, scaling);
};
